/**
 * CDM File Import — Track 1.2: CSV / XLSX file ingest into the CDM.
 *
 * Companion to /app/cdm/specs (REST/JSON specs): same end goal — get a
 * source's data into cdm_events without writing Python — but for the
 * worlds that only emit flat files. HR feeds, BMC Helix exports,
 * leavers spreadsheets from offboarding, etc.
 *
 * Flow:
 *   1. Pick a file (.csv or .xlsx)
 *   2. Set a source label (becomes csv:<label> in cdm_events)
 *   3. Pick an archetype
 *   4. Map source columns → CDM canonical fields (auto-suggested)
 *   5. Preview (5 rows) — no DB writes
 *   6. Import — runs through engine.absorb_many, returns ingest stats
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Upload, ChevronLeft, FileSpreadsheet, FileText, Play, CheckCircle2, XCircle,
  RefreshCw,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Canonical field catalogue per archetype ──────────────────────────────
// Kept short on purpose — covers the long-tail of HR / asset-feed fields
// without overwhelming the dropdown. Users can type freeform values too.
const ARCHETYPES: { value: string; label: string }[] = [
  { value: 'asset_laptop',          label: 'Asset — laptop' },
  { value: 'asset_server',          label: 'Asset — server' },
  { value: 'user',                  label: 'User / person' },
  { value: 'software_installation', label: 'Software installation' },
]

const CDM_FIELDS: Record<string, string[]> = {
  asset_laptop: [
    'hostname', 'fqdn', 'serial', 'mac', 'last_ip', 'os', 'os_version',
    'manufacturer', 'model', 'owner', 'department', 'site',
    'last_seen', 'state',
  ],
  asset_server: [
    'hostname', 'fqdn', 'serial', 'mac', 'last_ip', 'os', 'os_version',
    'manufacturer', 'model', 'owner', 'environment', 'site',
    'last_seen', 'state',
  ],
  user: [
    'upn', 'email', 'sam', 'full_name', 'department', 'manager',
    'employment_status', 'last_signin_at', 'state',
  ],
  software_installation: [
    'hostname', 'serial', 'product', 'vendor', 'publisher', 'version',
    'install_date', 'last_seen',
  ],
}

const NONE_FIELD = '__none__'

// ── Auto-suggest mapping ─────────────────────────────────────────────────
// Cheap O(n*m) string-distance — header names rarely number more than ~30.
function similarity(a: string, b: string): number {
  const sa = a.toLowerCase().replace(/[^a-z0-9]/g, '')
  const sb = b.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (!sa || !sb) return 0
  if (sa === sb) return 1
  if (sa.includes(sb) || sb.includes(sa)) return 0.85
  // simple bigram overlap
  const grams = (s: string) => {
    const out = new Set<string>()
    for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2))
    return out
  }
  const ga = grams(sa); const gb = grams(sb)
  if (!ga.size || !gb.size) return 0
  let common = 0
  ga.forEach(g => { if (gb.has(g)) common++ })
  return (2 * common) / (ga.size + gb.size)
}

// Aliases bias the auto-suggest towards what real exports actually call things
const FIELD_ALIASES: Record<string, string[]> = {
  hostname:     ['hostname', 'host', 'computername', 'devicename', 'machinename'],
  fqdn:         ['fqdn', 'dnsname', 'fullhost'],
  serial:       ['serial', 'serialnumber', 'assettag', 'assetid', 'service tag'],
  mac:          ['mac', 'macaddress', 'mac1', 'physicaladdress'],
  last_ip:      ['ip', 'ipaddress', 'lastip', 'ipv4'],
  os:           ['os', 'operatingsystem', 'osname', 'platform'],
  os_version:   ['osversion', 'kernelversion', 'build'],
  manufacturer: ['manufacturer', 'vendor', 'maker', 'brand'],
  model:        ['model', 'modelname', 'sku'],
  owner:        ['owner', 'assigneduser', 'assigned to', 'primaryuser'],
  department:   ['department', 'dept', 'businessunit', 'team'],
  email:        ['email', 'mail', 'workemail', 'owneremail'],
  upn:          ['upn', 'userprincipalname', 'login'],
  sam:          ['samaccountname', 'sam', 'username'],
  full_name:    ['displayname', 'fullname', 'name'],
  manager:      ['manager', 'reportsto', 'supervisor'],
  employment_status: ['status', 'employmentstatus', 'employmenttype'],
  last_signin_at: ['lastsignin', 'lastlogon', 'lastlogin'],
  product:      ['product', 'productname', 'applicationname'],
  vendor:       ['vendor', 'publisher', 'manufacturer'],
  publisher:    ['publisher', 'vendor'],
  version:      ['version', 'productversion'],
  install_date: ['installdate', 'installedon'],
  last_seen:    ['lastseen', 'lastcheckin', 'lastcontact'],
  environment:  ['environment', 'env', 'tier'],
  site:         ['site', 'location', 'office'],
  state:        ['state', 'status'],
}

function suggestMapping(columns: string[], cdmFields: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  const used = new Set<string>()
  for (const f of cdmFields) {
    const aliases = FIELD_ALIASES[f] || [f]
    let bestCol = ''
    let bestScore = 0.5  // confidence floor — below this we leave unmapped
    for (const c of columns) {
      if (used.has(c)) continue
      let score = 0
      for (const a of aliases) {
        const s = similarity(c, a)
        if (s > score) score = s
      }
      if (score > bestScore) { bestScore = score; bestCol = c }
    }
    if (bestCol) {
      out[f] = bestCol
      used.add(bestCol)
    }
  }
  return out
}

// ── Base64 helper (binary-safe, doesn't choke on UTF-8 bytes) ────────────
async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const bytes = new Uint8Array(buf)
  let s = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(s)
}

interface PreviewRecord {
  source_id: string
  archetype: string
  canonical: Record<string, unknown>
  match_keys: Record<string, unknown>
  raw: Record<string, unknown>
}

interface ImportStats {
  staged: number
  events_emitted: number
  new_goldens: number
  matched: number
  sent_to_review: number
  conflicts_opened: number
  duplicates_skipped: number
}

export default function CdmImportPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<'csv' | 'xlsx'>('csv')
  const [sourceLabel, setSourceLabel] = useState('')
  const [archetype, setArchetype] = useState('asset_laptop')
  const [columns, setColumns] = useState<string[]>([])
  const [rowCount, setRowCount] = useState<number | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [matchKeys, setMatchKeys] = useState<Record<string, boolean>>({})
  const [preview, setPreview] = useState<PreviewRecord[]>([])
  const [stats, setStats] = useState<ImportStats | null>(null)
  const [importedSource, setImportedSource] = useState('')
  const [busy, setBusy] = useState<'preview' | 'import' | null>(null)
  const [message, setMessage] = useState('')

  const cdmFields = useMemo(() => CDM_FIELDS[archetype] || [], [archetype])

  // Re-suggest mapping when archetype changes
  useEffect(() => {
    if (columns.length) {
      setMapping(suggestMapping(columns, cdmFields))
    }
  }, [archetype])  // eslint-disable-line react-hooks/exhaustive-deps

  // When file picked, pull header columns via /import/preview (empty mapping)
  const onFile = async (f: File) => {
    setFile(f)
    setStats(null)
    setPreview([])
    const lower = f.name.toLowerCase()
    const k: 'csv' | 'xlsx' = lower.endsWith('.xlsx') ? 'xlsx' : 'csv'
    setKind(k)
    if (!sourceLabel) {
      const stem = f.name.replace(/\.(csv|xlsx)$/i, '').slice(0, 64)
      setSourceLabel(stem)
    }
    try {
      setMessage('Reading file…')
      const content_b64 = await fileToBase64(f)
      const { data } = await api.post('/api/cdm/import/preview', {
        kind: k,
        content_b64,
        mapping: {},
        match_keys_map: {},
        archetype,
      })
      setColumns(data.columns || [])
      setRowCount(data.row_count ?? null)
      const suggested = suggestMapping(data.columns || [], cdmFields)
      setMapping(suggested)
      // Default match keys = fields the canonicaliser knows (mac/serial/upn/email/hostname)
      const mk: Record<string, boolean> = {}
      for (const f2 of Object.keys(suggested)) {
        if (['serial', 'mac', 'upn', 'email', 'hostname', 'fqdn'].includes(f2)) mk[f2] = true
      }
      setMatchKeys(mk)
      setMessage(`Detected ${data.columns?.length || 0} columns, ${data.row_count ?? '?'} rows`)
    } catch (e: any) {
      setMessage(`Could not parse file: ${e.response?.data?.detail || e.message}`)
      setColumns([])
      setRowCount(null)
    }
  }

  const buildBody = async () => {
    if (!file) throw new Error('No file picked')
    const content_b64 = await fileToBase64(file)
    const cleanMapping: Record<string, string> = {}
    for (const [k2, v] of Object.entries(mapping)) {
      if (v && v !== NONE_FIELD) cleanMapping[k2] = v
    }
    const cleanMatchKeys: Record<string, string> = {}
    for (const [k2, on] of Object.entries(matchKeys)) {
      if (on && cleanMapping[k2]) cleanMatchKeys[k2] = cleanMapping[k2]
    }
    return {
      kind, content_b64,
      mapping: cleanMapping,
      match_keys_map: cleanMatchKeys,
      archetype,
      source_label: sourceLabel || 'manual-upload',
    }
  }

  const doPreview = async () => {
    if (!file) { setMessage('Pick a file first'); return }
    setBusy('preview'); setMessage('Generating preview…')
    try {
      const body = await buildBody()
      const { data } = await api.post('/api/cdm/import/preview', body)
      setPreview(data.preview || [])
      setMessage(`Preview ready — ${data.preview?.length || 0} of ${data.row_count} rows shown`)
    } catch (e: any) {
      setMessage(`Preview failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const doImport = async () => {
    if (!file) { setMessage('Pick a file first'); return }
    if (!confirm(`Import ${rowCount ?? '?'} rows from "${file.name}" as ${archetype} into CDM?`)) return
    setBusy('import'); setMessage('Importing…')
    try {
      const body = await buildBody()
      const { data } = await api.post('/api/cdm/import', body)
      setStats(data.stats)
      setImportedSource(data.source || '')
      setMessage(`Imported — ${data.stats?.events_emitted ?? 0} events, ${data.stats?.new_goldens ?? 0} new goldens`)
    } catch (e: any) {
      setMessage(`Import failed: ${e.response?.data?.detail || e.message}`)
    } finally { setBusy(null) }
  }

  const reset = () => {
    setFile(null); setColumns([]); setRowCount(null)
    setMapping({}); setMatchKeys({}); setPreview([]); setStats(null)
    setSourceLabel(''); setMessage('')
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Upload size={18} className="text-cyan" /> CDM FILE IMPORT
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          CSV / XLSX → cdm_events · same engine as live adapters
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={reset} className="btn-ghost">
            <RefreshCw size={11} /> Reset
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Step 1 — file + label + archetype */}
        <div className="card p-3 space-y-3 lg:col-span-1">
          <div className="flex items-center gap-2">
            {kind === 'xlsx' ? <FileSpreadsheet size={14} className="text-emerald" /> : <FileText size={14} className="text-cyan" />}
            <h2 className="text-sm font-semibold text-text-primary">1. File & metadata</h2>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-text-muted">File</label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f) }}
              className="block w-full text-xs text-text-secondary file:mr-2 file:py-1 file:px-3 file:rounded file:border file:border-border file:bg-surface-raised file:text-text-primary file:text-xs file:cursor-pointer cursor-pointer"
            />
            {file && (
              <div className="text-[10px] text-text-muted mt-1 font-mono">
                {file.name} · {(file.size / 1024).toFixed(1)} KB · {kind}
                {rowCount !== null && ` · ${rowCount} rows`}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Source label</label>
            <input
              type="text"
              value={sourceLabel}
              onChange={e => setSourceLabel(e.target.value)}
              placeholder="hr-feed-q2"
              className="input w-full"
            />
            <div className="text-[10px] text-text-muted mt-1">
              Recorded as <code className="text-text-secondary">{kind}:{sourceLabel || '<label>'}</code> in cdm_events.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-text-muted">Archetype</label>
            <select
              value={archetype}
              onChange={e => setArchetype(e.target.value)}
              className="input w-full"
            >
              {ARCHETYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button type="button" onClick={doPreview} disabled={!file || busy !== null} className="btn-ghost">
              <Play size={11} /> {busy === 'preview' ? 'Previewing…' : 'Preview'}
            </button>
            <button type="button" onClick={doImport} disabled={!file || !preview.length || busy !== null} className="btn-primary">
              <Upload size={11} /> {busy === 'import' ? 'Importing…' : 'Import'}
            </button>
          </div>
          <div className="text-[10px] text-text-muted">
            Preview is dry-run, no DB writes. Import is admin-gated and writes through
            the engine's idempotency + matching layer — re-running won't double-count.
          </div>
        </div>

        {/* Step 2 — column → CDM-field mapping */}
        <div className="card p-3 space-y-2 lg:col-span-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-text-primary">2. Map columns to CDM fields</h2>
            <span className="text-[10px] text-text-muted">Auto-suggested by name similarity. Tick the strong identity fields for matching.</span>
          </div>

          {columns.length === 0 ? (
            <p className="text-[11px] text-text-muted py-6 text-center">
              Pick a file to detect its columns.
            </p>
          ) : (
            <div className="overflow-auto max-h-[480px] border border-border rounded">
              <table className="w-full text-xs">
                <thead className="bg-surface-raised sticky top-0">
                  <tr className="text-[10px] uppercase tracking-widest text-text-muted">
                    <th className="text-left px-3 py-2">Source column</th>
                    <th className="text-left px-3 py-2">CDM field</th>
                    <th className="text-center px-3 py-2" title="Use as identity-resolution key">Match key</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {columns.map(col => {
                    // Find which cdm field this column is currently mapped to
                    const mappedTo = Object.entries(mapping).find(([_, v]) => v === col)?.[0] || NONE_FIELD
                    return (
                      <tr key={col} className="hover:bg-surface-raised/40">
                        <td className="px-3 py-1.5 font-mono text-[11px] text-text-secondary">{col}</td>
                        <td className="px-3 py-1.5">
                          <select
                            value={mappedTo}
                            onChange={e => {
                              const newField = e.target.value
                              setMapping(prev => {
                                const next: Record<string, string> = {}
                                // Drop previous mapping that pointed to this column
                                for (const [k, v] of Object.entries(prev)) {
                                  if (v !== col) next[k] = v
                                }
                                if (newField !== NONE_FIELD) {
                                  // Also drop any other column previously mapped to that field
                                  delete next[newField]
                                  next[newField] = col
                                }
                                return next
                              })
                            }}
                            className="input w-full text-[11px]"
                          >
                            <option value={NONE_FIELD}>— skip —</option>
                            {cdmFields.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {mappedTo !== NONE_FIELD ? (
                            <input
                              type="checkbox"
                              checked={!!matchKeys[mappedTo]}
                              onChange={e => setMatchKeys(prev => ({ ...prev, [mappedTo]: e.target.checked }))}
                              className="cursor-pointer"
                            />
                          ) : <span className="text-text-muted">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Step 3 — preview output */}
      {preview.length > 0 && (
        <div className="card p-3 space-y-2">
          <h2 className="text-sm font-semibold text-text-primary">3. Preview ({preview.length} rows)</h2>
          <div className="text-[10px] text-text-muted">
            Canonical = what the engine will store. Match keys drive the Fellegi-Sunter scorer.
          </div>
          <div className="overflow-auto max-h-[420px] border border-border rounded">
            <table className="w-full text-[11px]">
              <thead className="bg-surface-raised sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted">
                  <th className="text-left px-3 py-2">source_id</th>
                  <th className="text-left px-3 py-2">canonical</th>
                  <th className="text-left px-3 py-2">match_keys</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {preview.map((p, i) => (
                  <tr key={i} className="hover:bg-surface-raised/40 align-top">
                    <td className="px-3 py-1.5 font-mono text-text-secondary">{p.source_id}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary whitespace-pre-wrap">
                      {Object.entries(p.canonical).map(([k, v]) => (
                        <div key={k}><span className="text-blue-light">{k}</span>: {String(v)}</div>
                      ))}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-secondary whitespace-pre-wrap">
                      {Object.entries(p.match_keys).map(([k, v]) => (
                        <div key={k}><span className="text-amber">{k}</span>: {String(v)}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 4 — import stats */}
      {stats && (
        <div className="card p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald" />
            <h2 className="text-sm font-semibold text-text-primary">Import complete</h2>
            <span className="text-[10px] text-text-muted font-mono">{importedSource}</span>
            <Link to="/app/cdm" className="ml-auto btn-ghost text-[11px]">View CDM</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: 'staged',             label: 'Staged'         },
              { k: 'events_emitted',     label: 'Events emitted' },
              { k: 'new_goldens',        label: 'New goldens'    },
              { k: 'matched',            label: 'Matched'        },
              { k: 'sent_to_review',     label: 'To review'      },
              { k: 'conflicts_opened',   label: 'Conflicts'      },
              { k: 'duplicates_skipped', label: 'Duplicates'     },
            ].map(s => (
              <div key={s.k} className="bg-surface-raised border border-border rounded p-2">
                <div className="text-[10px] uppercase tracking-widest text-text-muted">{s.label}</div>
                <div className="text-lg font-mono text-text-primary">
                  {(stats as any)[s.k] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help footer */}
      <div className="text-[10px] text-text-muted text-center">
        XLSX requires <code>openpyxl</code> on the backend. If your deployment hasn't shipped it,
        the API returns 415 — convert to CSV first.
        Conflicts and verification are handled by the same engine that processes live adapters,
        so re-importing the same file is idempotent at the (source, source_id, observed_at, field, value) level.
      </div>
    </div>
  )
}
